import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Button,
  DialogFooter,
  Form,
  FormField,
  FormInput,
  FormSelect,
  useMultiStepForm,
} from "web-design-system";
import {
  useCountryOptions,
  useForeignAddress,
  useInvestorProfile,
  useUpdateInvestorProfileDetails,
} from "creso-core/hooks";
import { TaxResidencySchema } from "creso-core/zod";
import { isTinNotApplicable, TIN_FORMATS } from "creso-core/constants";
import { Loader2Icon } from "lucide-react";
import { handleError } from "../../utils";
import { useClientOnboarding } from "./context";

/** NRI only: where the client is a tax resident */
export function TaxResidency() {
  const { client } = useClientOnboarding();
  const { previousStep, nextStep } = useMultiStepForm();
  const countryOptions = useCountryOptions();
  const investmentAccountGuid = client?.investment_account ?? "";
  const { data: profile } = useInvestorProfile(investmentAccountGuid);
  const { data: foreignAddress } = useForeignAddress(investmentAccountGuid);
  const { mutateAsync: updateProfile } = useUpdateInvestorProfileDetails();
  const residency = profile?.first_tax_residency;

  const form = useForm<z.infer<typeof TaxResidencySchema>>({
    resolver: zodResolver(TaxResidencySchema),
    values: {
      // Usually where they live, so the overseas address fills it in
      tax_residence_country:
        residency?.country ?? foreignAddress?.foreign_address_country ?? "",
      tin: residency?.taxid_number ?? "",
    },
  });

  const taxCountry = form.watch("tax_residence_country");
  // Countries without a TIN leave it empty
  const tinNotApplicable = isTinNotApplicable(taxCountry);
  const tinExample = TIN_FORMATS[taxCountry]?.example;
  let tinPlaceholder = tinExample ? `e.g. ${tinExample}` : "Enter TIN";
  if (tinNotApplicable) tinPlaceholder = "Not applicable for this country";
  useEffect(() => {
    if (!tinNotApplicable) return;
    form.setValue("tin", "");
    form.clearErrors("tin");
  }, [tinNotApplicable, form]);

  const onSubmit = async (data: z.infer<typeof TaxResidencySchema>) => {
    try {
      await updateProfile({
        investment_account_guid: investmentAccountGuid,
        first_tax_residency: {
          country: data.tax_residence_country,
          taxid_type: "tin",
          taxid_number: tinNotApplicable ? null : data.tin.trim(),
        },
      });
      nextStep();
    } catch (error) {
      handleError(error, "Failed to save tax residency");
    }
  };

  return (
    <div className="flex flex-col size-full p-6 gap-6">
      <span className="text-lg font-semibold">Tax residency</span>
      <Form {...form}>
        <form
          className="flex flex-col flex-1 gap-4"
          onSubmit={form.handleSubmit(onSubmit)}
        >
          <FormField
            control={form.control}
            name="tax_residence_country"
            render={({ field }) => (
              <FormSelect
                label="Country of tax residence"
                placeholder="Select country"
                {...field}
                options={countryOptions}
              />
            )}
          />
          <FormField
            control={form.control}
            name="tin"
            render={({ field }) => (
              <FormInput
                label="Tax Identification Number (TIN)"
                placeholder={tinPlaceholder}
                maxLength={20}
                {...field}
                disabled={tinNotApplicable}
              />
            )}
          />
          <DialogFooter className="mt-auto flex sm:flex-col gap-4">
            <div className="flex items-center w-full gap-5">
              <Button type="button" variant="outline" onClick={previousStep}>
                Previous
              </Button>
              <Button
                className="flex-1"
                type="submit"
                disabled={form.formState.isSubmitting}
              >
                {form.formState.isSubmitting && (
                  <Loader2Icon className="size-4 animate-spin" />
                )}
                Next
              </Button>
            </div>
          </DialogFooter>
        </form>
      </Form>
    </div>
  );
}
